import React, { useState, useEffect } from "react";

/* PACKAGES */
import { Helmet } from "react-helmet";
import DataTable from "react-data-table-component";
import {
  Button,
  Card,
  CardBody,
  Dialog,
  DialogBody,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import moment from "moment";
import toast from "react-hot-toast";

/* API */
import { deleteFeedback, getFeedback } from "service/api/auth.api";

/* ICONS */
import { GoKebabHorizontal } from "react-icons/go";
import { IoClose } from "react-icons/io5";

/* LOADING SCREEN FOR DATATABLE */
const LoadingScreen = () => {
  return (
    <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
      <p className="text-sm font-ibm-regular text-black text-center">
        Please wait!
      </p>
      <p className="text-sm font-ibm-regular text-black text-center">
        We are fetching data{" "}
      </p>
    </div>
  );
};

/* NO DATA SCREEN */
const NoDataScreen = () => {
  return (
    <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
      <p className="text-sm font-ibm-regular text-black text-center">
        There are no students to display.
      </p>
    </div>
  );
};

const Feedback = () => {
  const [loading, setLoading] = useState(false);

  /* PAGE */
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  /* TOTAL DOCS */
  const [totalDocs, setTotalDocs] = useState(0);

  /* LIST OF DATA */
  const [dataList, setDataList] = useState([]);

  /* DETAILS */
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  /* AWARD CATEORY */
  const [category, setCategory] = useState(null);

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
  };

  /* HANDLE PER PAGE */
  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
  };

  /* GET LIST OF DATA */
  const getListOfData = async () => {
    setLoading(true);

    const params = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };

    getFeedback(params)
      .then((res) => {
        setDataList(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("Error: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getListOfData();
  }, [page, sizePerPage]);

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => (
        <span className="text-sm font-ibm-regular text-black text-opacity-60">
          {index + 1}
        </span>
      ),
    },
    {
      name: "Name and Phone",
      grow: 1,
      selector: (row) => (
        <React.Fragment>
          <p className="text-sm font-ibm-semibold text-black">
            {row?.firstName} {row?.lastName}
          </p>
          <a
            href={`mailto:${row?.phoneNumber}`}
            className="block mt-1 text-blue-400 font-ibm-regular"
          >
            {row?.phoneNumber}
          </a>
        </React.Fragment>
      ),
    },
    {
      name: "Award category",
      grow: 1.5,
      selector: (row) => (
        <p className="text-sm font-ibm-regular text-black">
          {row?.awardCategory}
        </p>
      ),
    },
    {
      name: "Submitted on",
      grow: 1,
      selector: (row) => (
        <React.Fragment>
          <p
            style={{ overflowWrap: "anywhere" }}
            className="text-sm font-ibm-regular text-greyText pr-5"
          >
            {moment(row.createdAt).format("DD-MM-YYYY")}
          </p>
          <p
            style={{ overflowWrap: "anywhere" }}
            className="mt-1.5 text-sm font-ibm-regular text-greyText pr-5"
          >
            {moment(row.createdAt).format("HH:mm A")}
          </p>
        </React.Fragment>
      ),
    },
    {
      name: "Actions",
      selector: (row) => (
        <React.Fragment>
          <Menu>
            <MenuHandler>
              <Button className="shadow-none hover:shadow-none p-1.5 bg-greyBg border border-greyBorder hover:border-black/40 rounded-full transition-all duration-300 ease-in-out">
                <GoKebabHorizontal className="w-4 h-4 text-greyText rotate-90" />
              </Button>
            </MenuHandler>
            <MenuList className="p-1.5 min-w-[20vh] bg-white">
              <MenuItem
                onClick={() => {
                  setSelectedDetails(row);
                  setViewDetailsDialog(true);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                View details
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelectedDetails(row);
                  setDeleteModal(true);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Delete entry
              </MenuItem>
            </MenuList>
          </Menu>
        </React.Fragment>
      ),
    },
  ];

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    setLoading(true);
    deleteFeedback(selectedDetails?._id)
      .then((res) => {
        toast.success(res?.message || "Entry deleted successfully");
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setDeleteModal(false);
        setLoading(false);
        getListOfData();
      });
  };

  return (
    <section className="min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Feedback form | Admin Panel</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="p-0">
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Feedbacks
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage and view the feedback for Shree Saraswati Sanman Awards
              2025
            </h2>
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="mt-5">
          <Card className="bg-white border border-greyBorder rounded-lg">
            <CardBody className="p-5">
              <DataTable
                columns={columns}
                data={dataList}
                progressPending={loading}
                progressComponent={<LoadingScreen />}
                noDataComponent={<NoDataScreen />}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50, 100]}
                paginationTotalRows={totalDocs}
                onChangeRowsPerPage={handlePerPageChange}
                onChangePage={handlePageChange}
                className="data-table"
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* VIEW DIALOG */}
      <Dialog open={viewDetailsDialog} handler={setViewDetailsDialog} size="sm">
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-2.5 border-b border-[#E5E5E5] flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Feedback details
            </h5>
            <button
              onClick={() => setViewDetailsDialog(!viewDetailsDialog)}
              className="shadow-none p-2 rounded-full bg-white hover:bg-black/5 transition-all duration-200 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-[#777]" />
            </button>
          </div>

          {/* BODY */}
          <div className="px-5 max-h-[70vh] overflow-auto">
            <div className="py-5 border-b border-greyBorder">
              <div className="grid grid-cols-2 gap-5">
                <div className="col-span-1">
                  <label className="text-sm font-ibm-regular text-greyText">
                    First name
                  </label>
                  <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                    {selectedDetails?.firstName}
                  </p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Last name
                  </label>
                  <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                    {selectedDetails?.lastName}
                  </p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Phone number
                  </label>
                  <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                    {selectedDetails?.phoneNumber}
                  </p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Award category
                  </label>
                  <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                    {selectedDetails?.awardCategory}
                  </p>
                </div>
              </div>
            </div>

            <div className="py-5 border-b border-greyBorder">
              <div className="block">
                <p className="text-sm font-ibm-regular text-greyText">
                  Did you attend the awards ceremony in person?
                </p>
                <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                  {selectedDetails?.isAttendedAwardFunction}
                </p>
              </div>

              {selectedDetails?.isAttendedAwardFunction !== "No" && (
                <div className="mt-5">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Were the following items presented during the awards
                    ceremony?
                  </p>
                  <div className="mt-2 flex items-center gap-5">
                    <label className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        disabled
                        checked={
                          selectedDetails?.isFollowingItemsPresented?.trophy
                        }
                        className="w-4 h-4"
                      />
                      <p className="text-sm font-ibm-regular text-black">
                        Trophy
                      </p>
                    </label>
                    <label className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        disabled
                        checked={
                          selectedDetails?.isFollowingItemsPresented
                            ?.certificate
                        }
                        className="w-4 h-4"
                      />
                      <p className="text-sm font-ibm-regular text-black">
                        Certificate
                      </p>
                    </label>
                    <label className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        disabled
                        checked={
                          selectedDetails?.isFollowingItemsPresented
                            ?.lmpLogoFrame
                        }
                        className="w-4 h-4"
                      />
                      <p className="text-sm font-ibm-regular text-black">
                        LMP Logo Frame
                      </p>
                    </label>
                    {selectedDetails?.awardCategory !==
                      "Excellence in Teaching Profession" ||
                    selectedDetails?.awardCategory !==
                      "Outstanding Educational Institutions" ? (
                      <label className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          disabled
                          checked={
                            selectedDetails?.isFollowingItemsPresented
                              ?.priceMoney
                          }
                          className="w-4 h-4"
                        />
                        <p className="text-sm font-ibm-regular text-black">
                          Price Money
                        </p>
                      </label>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )}

              {selectedDetails?.missingItems !== null &&
                selectedDetails?.missingItems[0] !== "" && (
                  <div className="mt-5">
                    <p className="text-sm font-ibm-regular text-greyText">
                      If not received, please specify which item(s) are pending
                    </p>
                    <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                      {selectedDetails?.missingItems[0]}
                    </p>
                  </div>
                )}
            </div>

            <div className="py-5">
              <div className="mt-0">
                <p className="text-sm font-ibm-regular text-greyText">
                  Application/Registration Process:{" "}
                  <span className="text-black font-ibm-semibold">
                    {
                      selectedDetails?.eventExperience?.registrationProcess
                        ?.score
                    }
                    /5 score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.registrationProcess
                  ?.feedback !== "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {
                      selectedDetails?.eventExperience?.registrationProcess
                        ?.feedback
                    }
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Communications & Instructions:{" "}
                  <span className="text-black font-ibm-semibold">
                    {selectedDetails?.eventExperience?.communication?.score}/5{" "}
                    score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.communication?.feedback !==
                  "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {selectedDetails?.eventExperience?.communication?.feedback}
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Jury & Selection Process:{" "}
                  <span className="text-black font-ibm-semibold">
                    {
                      selectedDetails?.eventExperience?.jurySelectionProcess
                        ?.score
                    }
                    /5 score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.jurySelectionProcess
                  ?.feedback !== "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {
                      selectedDetails?.eventExperience?.jurySelectionProcess
                        ?.feedback
                    }
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Venue Setup & Arrangements:{" "}
                  <span className="text-black font-ibm-semibold">
                    {
                      selectedDetails?.eventExperience?.venueSetupArrangements
                        ?.score
                    }
                    /5 score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.venueSetupArrangements
                  ?.feedback !== "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {
                      selectedDetails?.eventExperience?.venueSetupArrangements
                        ?.feedback
                    }
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Hospitality (Travel & Accommodation assistance):{" "}
                  <span className="text-black font-ibm-semibold">
                    {selectedDetails?.eventExperience?.hospitality?.score}/5
                    score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.hospitality?.feedback !==
                  "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {selectedDetails?.eventExperience?.hospitality?.feedback}
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Food & Catering (Breakfast, Lunch, High Tea) :{" "}
                  <span className="text-black font-ibm-semibold">
                    {selectedDetails?.eventExperience?.foodCatering?.score}/5{" "}
                    score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.foodCatering?.feedback !==
                  "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {selectedDetails?.eventExperience?.foodCatering?.feedback}
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Award Presentation & Felicitation :{" "}
                  <span className="text-black font-ibm-semibold">
                    {
                      selectedDetails?.eventExperience
                        ?.awardPresentationFelicitation?.score
                    }
                    /5 score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.awardPresentationFelicitation
                  ?.feedback !== "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {
                      selectedDetails?.eventExperience
                        ?.awardPresentationFelicitation?.feedback
                    }
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Overall Management & Coordination Experience :{" "}
                  <span className="text-black font-ibm-semibold">
                    {
                      selectedDetails?.eventExperience
                        ?.overallManagementCoordination?.score
                    }
                    /5 score
                  </span>
                </p>
                {selectedDetails?.eventExperience?.overallManagementCoordination
                  ?.feedback !== "" && (
                  <p className="mt-1 text-sm font-ibm-regular text-black">
                    <span className="text-greyText">Feedback:</span>{" "}
                    {
                      selectedDetails?.eventExperience
                        ?.overallManagementCoordination?.feedback
                    }
                  </p>
                )}
              </div>

              {selectedDetails?.interestingActivities !== null && (
                <div className="mt-5">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Which other activities of Shree Lohana Mahaparishad would
                    you be interested in?
                  </p>
                  <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                    {selectedDetails?.interestingActivities[0]}
                  </p>
                </div>
              )}

              <div className="mt-5">
                <p className="text-sm font-ibm-regular text-greyText">
                  Would you like to support Shree Lohana Mahaparishad in its
                  community initiatives?
                </p>
                <p className="mt-1 bg-gray-100 rounded-md px-4 py-2 text-sm font-ibm-regular text-black border border-greyBorder">
                  {selectedDetails?.likeToBePartOfLMP}
                </p>
              </div>

              {selectedDetails?.testimonialLink !== "Not entered" && (
                <div className="mt-5">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Video testimonial
                  </p>
                  <video
                    controls
                    className="mt-2 w-1/2 h-[25vh] object-cover rounded-lg"
                  >
                    <source
                      src={`https://storage.googleapis.com/kidsstreet-test/${selectedDetails?.testimonialLink}`}
                      type="video/mp4"
                    />
                  </video>
                </div>
              )}
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* DELETE MODAL */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Delete entry
              </h5>
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to delete this feedback entry? This action
              cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                {loading ? "Deleting entry..." : "Delete entry"}
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default Feedback;
